// components/ServiceOrderMapView.tsx
import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import mapboxgl from "mapbox-gl";
import { Button } from "@shared/ui/button";
import { Badge } from "@shared/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@shared/ui/card";
import {
  ArrowLeft,
  MapPin,
  Clock,
  User,
  Phone,
  Building,
  AlertCircle,
  Info,
} from "lucide-react";
import { getPriorityColor, getStatusColor } from "../../../../utils";

// Import Mapbox CSS
import "mapbox-gl/dist/mapbox-gl.css";

interface ServiceOrderMapViewProps {
  serviceOrders: any[];
  allServiceOrders: any[];
  onServiceOrderClick: (order: any) => void;
  onClearSelection: () => void;
  onBackToList: () => void;
  selectedCount: number;
}

// Set Mapbox access token from environment variable
mapboxgl.accessToken = import.meta.env.VITE_MAPBOX_ACCESS_TOKEN || "";

export const ServiceOrderMapView: React.FC<ServiceOrderMapViewProps> = ({
  serviceOrders,
  allServiceOrders,
  onServiceOrderClick,
  onClearSelection,
  onBackToList,
  selectedCount,
}) => {
  const navigate = useNavigate();
  const mapContainer = useRef<HTMLDivElement>(null);
  const map = useRef<mapboxgl.Map | null>(null);
  const markersRef = useRef<mapboxgl.Marker[]>([]);
  const [mapLoaded, setMapLoaded] = useState(false);

  // Filter orders that have valid coordinates
  const ordersWithCoordinates = serviceOrders.filter(
    (order) => order.latitude !== null && order.longitude !== null
  );

  const ordersWithoutCoordinates = serviceOrders.filter(
    (order) => order.latitude === null || order.longitude === null
  );

  // Priority color mapping for markers
  const getPriorityMarkerColor = (priority: string) => {
    switch (priority?.toLowerCase()) {
      case "critical":
        return "#dc2626"; // red-600
      case "high":
        return "#ea580c"; // orange-600
      case "medium":
        return "#ca8a04"; // yellow-600
      case "low":
        return "#16a34a"; // green-600
      default:
        return "#6b7280"; // gray-500
    }
  };

  // Status color mapping for markers
  const getStatusMarkerColor = (status: string) => {
    switch (status?.toLowerCase()) {
      case "created":
        return "#2563eb"; // blue-600
      case "overdue":
        return "#dc2626"; // red-600
      case "in progress":
        return "#ca8a04"; // yellow-600
      case "completed":
        return "#16a34a"; // green-600
      default:
        return "#6b7280"; // gray-500
    }
  };

  // Handle view details click with proper routing
  const handleViewServiceOrder = (order: any) => {
    const association = order.serviceAssociation?.toLowerCase();
    const targetRoute = (() => {
      switch (association) {
        case "meter":
          return `/service-orders/dispatcher/pending-meter-service-order/${order.id}`;
        case "consumer":
          return `/service-orders/dispatcher/pending-consumer-service-order/${order.id}`;
        case "asset":
          return `/service-orders/dispatcher/pending-asset-service-order/${order.id}`;
        default:
          return `/service-orders/dispatcher/pending-service-order/${order.id}`;
      }
    })();

    navigate(targetRoute);
  };

  // Create popup content for service order
  const createPopupContent = (order: any) => {
    const priorityClass = getPriorityColor(order.priority);
    const statusClass = getStatusColor(order.statusDisplay);

    return `
      <div class="p-3 min-w-72 max-w-sm">
        <div class="flex items-center justify-between mb-3">
          <h3 class="font-semibold text-gray-900 text-sm">${
            order.job_card_no || order.id
          }</h3>
          <span class="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${priorityClass}">
            ${order.priority}
          </span>
        </div>
        
        <div class="space-y-2 text-sm">
          <div class="flex items-center gap-2">
            <span class="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${statusClass}">
              ${order.statusDisplay}
            </span>
            <span class="text-gray-600">${order.serviceAssociation}</span>
          </div>
          
          ${
            order.name
              ? `<div class="font-medium text-gray-900">${order.name}</div>`
              : ""
          }
          
          ${
            order.areaName
              ? `
            <div class="flex items-center gap-1 text-gray-600">
              <svg class="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z"/>
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 11a3 3 0 11-6 0 3 3 0 016 0z"/>
              </svg>
              ${order.areaName}
            </div>
          `
              : ""
          }
          
          ${
            order.subAreaName
              ? `
            <div class="text-xs text-gray-500 ml-4">${order.subArea}</div>
          `
              : ""
          }
          
          ${
            order.mobile_number
              ? `
            <div class="flex items-center gap-1 text-gray-600">
              <svg class="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z"/>
              </svg>
              ${order.mobile_number}
            </div>
          `
              : ""
          }
          
          ${
            order.request_account_no
              ? `
            <div class="text-xs text-gray-500">
              Account: ${order.request_account_no}
            </div>
          `
              : ""
          }
          
          ${
            order.created_user_remote_name
              ? `
            <div class="flex items-center gap-1 text-gray-600">
              <svg class="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"/>
              </svg>
              Created by: ${order.created_user_remote_name}
            </div>
          `
              : ""
          }
          
          ${
            order.createdOn
              ? `
            <div class="flex items-center gap-1 text-gray-600">
              <svg class="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"/>
              </svg>
              ${order.createdOn}
            </div>
          `
              : ""
          }
          
          ${
            order.expected_end_date
              ? `
            <div class="text-xs text-gray-500">
              Expected: ${order.expected_end_date}
            </div>
          `
              : ""
          }
          
          ${
            order.sop_utility_service?.length
              ? `
            <div class="flex flex-wrap gap-1 mt-2">
              ${order.sop_utility_service
                .map(
                  (service: string) =>
                    `<span class="inline-flex items-center px-2 py-1 rounded text-xs bg-blue-100 text-blue-800">${service}</span>`
                )
                .join("")}
            </div>
          `
              : ""
          }
          
          ${
            order.additional_data?.notes
              ? `
            <div class="mt-2 p-2 bg-gray-50 rounded text-xs">
              <strong>Notes:</strong> ${order.additional_data.notes}
            </div>
          `
              : ""
          }
        </div>
        
        <div class="mt-3 pt-2 border-t border-gray-200">
          <button 
            onclick="window.handleServiceOrderView('${order.id}')" 
            class="w-full bg-blue-600 hover:bg-blue-700 text-white text-xs font-medium py-2 px-3 rounded transition-colors"
          >
            View Details
          </button>
        </div>
      </div>
    `;
  };

  // Initialize map
  useEffect(() => {
    console.log(
      "All order IDs:",
      serviceOrders.map((o) => ({ id: o.id, type: typeof o.id }))
    );
    if (!mapContainer.current || map.current) return;

    // Check if Mapbox token is available
    if (!mapboxgl.accessToken) {
      console.error(
        "Mapbox access token is not set. Please add REACT_APP_MAPBOX_ACCESS_TOKEN to your .env file"
      );
      return;
    }

    map.current = new mapboxgl.Map({
      container: mapContainer.current,
      style: "mapbox://styles/mapbox/streets-v12",
      center: [-74.0066, 40.7135], // Default to NYC
      zoom: 10,
    });

    map.current.on("load", () => {
      setMapLoaded(true);
    });

    // Add global function for popup button clicks - Updated to use proper routing
    (window as any).handleServiceOrderView = (orderId: string) => {
      console.log(
        "Global function called with orderId:",
        orderId,
        typeof orderId
      );

      // Try to find in serviceOrders first, then allServiceOrders
      let order = serviceOrders.find((o) => String(o.id) === String(orderId));

      if (!order) {
        order = allServiceOrders.find((o) => String(o.id) === String(orderId));
      }

      if (order) {
        handleViewServiceOrder(order);
      } else {
        console.log(
          "Available IDs in serviceOrders:",
          serviceOrders.map((o) => o.id)
        );
        console.log(
          "Available IDs in allServiceOrders:",
          allServiceOrders.map((o) => o.id)
        );
      }
    };

    // Cleanup function
    return () => {
      if (map.current) {
        map.current.remove();
        map.current = null;
      }
      delete (window as any).handleServiceOrderView;
    };
  }, [serviceOrders, navigate]); // Added navigate to dependencies

  // Update markers when service orders change
  useEffect(() => {
    if (!map.current || !mapLoaded) return;

    // Clear existing markers
    markersRef.current.forEach((marker) => marker.remove());
    markersRef.current = [];

    // Add markers for orders with coordinates
    ordersWithCoordinates.forEach((order) => {
      const lat = parseFloat(order.latitude);
      const lng = parseFloat(order.longitude);

      if (isNaN(lat) || isNaN(lng)) return;

      // Create custom marker element
      const markerEl = document.createElement("div");
      markerEl.className = "custom-marker";
      markerEl.style.width = "24px";
      markerEl.style.height = "24px";
      markerEl.style.borderRadius = "50%";
      markerEl.style.backgroundColor = getPriorityMarkerColor(order.priority);
      markerEl.style.border = "2px solid white";
      markerEl.style.boxShadow = "0 2px 4px rgba(0,0,0,0.3)";
      markerEl.style.cursor = "pointer";
      markerEl.style.display = "flex";
      markerEl.style.alignItems = "center";
      markerEl.style.justifyContent = "center";

      // Add icon to marker
      markerEl.innerHTML = `
        <svg width="12" height="12" fill="white" viewBox="0 0 24 24">
          <path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7z"/>
        </svg>
      `;

      // Create popup
      const popup = new mapboxgl.Popup({
        offset: 25,
        closeButton: true,
        closeOnClick: false,
        maxWidth: "320px",
      }).setHTML(createPopupContent(order));

      // Create marker
      const marker = new mapboxgl.Marker(markerEl)
        .setLngLat([lng, lat])
        .setPopup(popup)
        .addTo(map.current!);

      markersRef.current.push(marker);
    });

    // Fit map to show all markers
    if (ordersWithCoordinates.length > 0) {
      const bounds = new mapboxgl.LngLatBounds();

      ordersWithCoordinates.forEach((order) => {
        const lat = parseFloat(order.latitude);
        const lng = parseFloat(order.longitude);
        if (!isNaN(lat) && !isNaN(lng)) {
          bounds.extend([lng, lat]);
        }
      });

      map.current.fitBounds(bounds, {
        padding: 50,
        maxZoom: 15,
      });
    }
  }, [ordersWithCoordinates, mapLoaded]);

  return (
    <div className="space-y-4">
      {/* Map Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Button
            variant="outline"
            size="sm"
            onClick={onBackToList}
            className="flex items-center gap-2"
          >
            <ArrowLeft className="h-4 w-4" />
            Back to List
          </Button>
          <div className="text-sm text-gray-600">
            Showing {ordersWithCoordinates.length} of {selectedCount} selected
            orders on map
          </div>
        </div>
        <Button
          variant="ghost"
          size="sm"
          onClick={onClearSelection}
          className="text-blue-600 hover:text-blue-700"
        >
          Clear Selection
        </Button>
      </div>

      {/* Map Stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-2">
              <MapPin className="h-5 w-5 text-green-600" />
              <div>
                <div className="text-2xl font-bold ">
                  {ordersWithCoordinates.length}
                </div>
                <div className="text-sm text-gray-600">With Coordinates</div>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-2">
              <AlertCircle className="h-5 w-5 text-orange-600" />
              <div>
                <div className="text-2xl font-bold ">
                  {ordersWithoutCoordinates.length}
                </div>
                <div className="text-sm text-gray-600">Missing Coordinates</div>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-2">
              <Info className="h-5 w-5 text-blue-600" />
              <div>
                <div className="text-2xl font-bold">{selectedCount}</div>
                <div className="text-sm text-gray-600">Total Selected</div>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Missing Coordinates Warning */}
      {ordersWithoutCoordinates.length > 0 && (
        <div className="bg-orange-50 border border-orange-200 rounded-lg p-4">
          <div className="flex items-start gap-3">
            <AlertCircle className="h-5 w-5 text-orange-600 mt-0.5" />
            <div>
              <h4 className="font-medium text-orange-800">
                {ordersWithoutCoordinates.length} orders missing coordinates
              </h4>
              <p className="text-sm text-orange-700 mt-1">
                The following service orders don't have location data and won't
                appear on the map:
              </p>
              <div className="mt-2 flex flex-wrap gap-2">
                {ordersWithoutCoordinates.slice(0, 5).map((order) => (
                  <Badge
                    key={order.id}
                    variant="outline"
                    className="text-orange-700 border-orange-300"
                  >
                    {order.job_card_no || order.id}
                  </Badge>
                ))}
                {ordersWithoutCoordinates.length > 5 && (
                  <Badge
                    variant="outline"
                    className="text-orange-700 border-orange-300"
                  >
                    +{ordersWithoutCoordinates.length - 5} more
                  </Badge>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Map Container */}
      <Card>
        <CardContent className="p-0">
          {!mapboxgl.accessToken ? (
            <div className="h-96 flex items-center justify-center bg-gray-50">
              <div className="text-center">
                <AlertCircle className="h-12 w-12 text-red-500 mx-auto mb-4" />
                <h3 className="text-lg font-medium text-gray-900 mb-2">
                  Mapbox Token Required
                </h3>
                <p className="text-gray-600 max-w-md">
                  Please add your Mapbox access token to the .env file as:
                  <br />
                  <code className="bg-gray-100 px-2 py-1 rounded text-sm mt-2 inline-block">
                    REACT_APP_MAPBOX_ACCESS_TOKEN=your_token_here
                  </code>
                </p>
              </div>
            </div>
          ) : (
            <div
              ref={mapContainer}
              className="w-full h-96 rounded-lg"
              style={{ minHeight: "400px" }}
            />
          )}
        </CardContent>
      </Card>

      {/* Legend */}
      <Card>
        <CardHeader>
          <CardTitle className="text-sm">Map Legend</CardTitle>
        </CardHeader>
        <CardContent className="p-4">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
            <div className="flex items-center gap-2">
              <div className="w-4 h-4 rounded-full bg-red-600"></div>
              <span>Critical Priority</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-4 h-4 rounded-full bg-orange-600"></div>
              <span>High Priority</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-4 h-4 rounded-full bg-yellow-600"></div>
              <span>Medium Priority</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-4 h-4 rounded-full bg-green-600"></div>
              <span>Low Priority</span>
            </div>
          </div>
          <div className="mt-2 text-xs text-gray-500">
            Click on any marker to view service order details
          </div>
        </CardContent>
      </Card>
    </div>
  );
};
